import { differenceInSeconds, startOfDay } from "date-fns"
import { FlightlineEvent, FlightlineEventDTO } from "../models/FlightlineEvent"
import { Sortie, SortieDto } from "../models/Sortie"
import { MissionShippingInfoWithReceiptDto, MissionShippingInfoWrapper } from "../models/missions/ShippingInfo"
import { FlightplanType } from "../store/types/FlightPlansTypes"
import { MissionGetDto, MissionParentPutDto, MissionPutDto, MissionType } from "../store/types/MissionsTypes"
import { compactFlownDate } from "../tools"
import { unplannedLineValue } from "./FlightlineEventUtils"
import { TimeUtilsDateFns } from "./TimeUtils"

export class MissionUtils {
    static calculateSortieDuration = (sortie: Sortie) => {
        const { fe_start_sortie, fe_stop_sortie } = sortie

        // Duration calculation
        return fe_start_sortie != null && fe_stop_sortie != null
            ? differenceInSeconds(fe_stop_sortie, fe_start_sortie)
            : undefined
    }

    static transformFlightPlansToOptions = (flightPlans: FlightplanType[]) => {
        return flightPlans.map((plan) => ({
            id: plan.flight_plan_id,
            flightplan_name: plan.flight_plan_name ? plan.flight_plan_name : "",
            label: `Flight Plan ${plan.flight_plan_id}`,
            status: plan.status,
        }))
    }

    static transformShippingInfoDtoToShippingInfo = (
        shippingInfo: MissionShippingInfoWithReceiptDto,
        id: number,
    ): MissionShippingInfoWrapper => {
        return {
            shipping_info: shippingInfo,
            data_receipt: shippingInfo.data_receipt,
            fe_id: id,
        }
    }

    static transformShippingWrapperToShippingInfoDto = (
        wrapper: MissionShippingInfoWrapper,
        mission: MissionType,
    ): MissionShippingInfoWithReceiptDto => {
        const shippingInfo = wrapper.shipping_info
        return {
            id: shippingInfo.id,
            office: shippingInfo.office,
            delivery_drive_name: shippingInfo.delivery_drive_name,
            raw_storage_size_gb: shippingInfo.raw_storage_size_gb,
            shipping_number: shippingInfo.shipping_number,
            // TODO = remove backup and plane drive name once moved to mission
            mission_id: mission.mission_id ?? undefined,
            backup_drive_name: mission.backup_drive_name,
            plane_drive_name: mission.plane_drive_name,
        }
    }

    static transformMissionDTOToMission = (mission: MissionGetDto): MissionType => {
        const transformSortiesDtoToSorties = (sortiesDto: SortieDto[]): Sortie[] => {
            const date = startOfDay(new Date())

            return sortiesDto.map((sortieDto, index) => {
                const { start_sortie, stop_sortie } = sortieDto
                return {
                    ...sortieDto,
                    frontend_id: index + 1,
                    fe_start_sortie: start_sortie ? new Date(start_sortie) : date,
                    fe_stop_sortie: stop_sortie ? new Date(stop_sortie) : date,
                }
            })
        }

        return {
            ...mission,
            // TODO - we're going to move these fields to mission on the backend soon
            //  for now this makes it easier to only edit these without making a new shipment
            plane_drive_name: mission.mission_shipping_info?.plane_drive_name,
            backup_drive_name: mission.mission_shipping_info?.backup_drive_name,
            mission_name: mission.mission_name ?? "",
            sorties: transformSortiesDtoToSorties(mission.sorties ?? []),
        }
    }

    static transformMissionToMissionDTO = (
        mission: MissionType,
        flightlineEvents: FlightlineEvent[],
        allShippingWrappers: MissionShippingInfoWrapper[],
        bools: { isVendorFlow: boolean; isNoFlight: boolean; isNoFile: boolean },
    ): MissionParentPutDto => {
        const { isVendorFlow, isNoFlight, isNoFile } = bools
        const sorties = mission.sorties
        const firstSortie = sorties[0]
        const lastSortie = sorties[sorties.length - 1]

        let firstDate = mission.flown_date
        let lastDate = mission.flown_date
        if (firstSortie && lastSortie) {
            firstDate = TimeUtilsDateFns.formatDate(firstSortie.fe_start_sortie)
            lastDate = TimeUtilsDateFns.formatDate(lastSortie.fe_stop_sortie)
        }

        const flightlineEventDto: FlightlineEventDTO[] = flightlineEvents.map((fl) => {
            // Deconstructs the event and removes fe_name_from_original which is a frontend only field
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { fe_name_from_original, fe_event_id, fe_planned_line_ids, ...rest } = fl

            const plannedLineIds = fe_planned_line_ids.filter((id) => id !== unplannedLineValue)

            const eventDTO: FlightlineEventDTO = { ...rest, planned_line_ids: plannedLineIds }

            if (isNoFile) {
                if (!fl.flight_line_start_time) {
                    eventDTO.flight_line_start_time = `${firstDate!} 12:00:00.000`
                }
                if (!fl.flight_line_stop_time) {
                    eventDTO.flight_line_stop_time = `${lastDate!} 12:30:00.000`
                }
            }
            return eventDTO
        })

        const shippingInfoDto = allShippingWrappers.map((wrapper) =>
            this.transformShippingWrapperToShippingInfoDto(wrapper, mission),
        )

        const sortiesDto: SortieDto[] = sorties.map((sortie) => {
            const { frontend_id, fe_start_sortie, fe_stop_sortie, ...restSortie } = sortie
            const start_sortie = TimeUtilsDateFns.formatISOStringLocal(fe_start_sortie)
            const stop_sortie = TimeUtilsDateFns.formatISOStringLocal(fe_stop_sortie)
            return { ...restSortie, start_sortie, stop_sortie, duration: this.calculateSortieDuration(sortie) }
        })

        const missionDto: MissionPutDto = { ...mission, sorties: sortiesDto }

        // default team or vendor values if the other is used
        if (isVendorFlow) {
            missionDto.operator_1_id = null
            missionDto.operator_2_id = null
            missionDto.pilot_1_id = null
            missionDto.pilot_2_id = null
        } else {
            missionDto.vendor_name = null
        }

        if (isNoFlight) {
            missionDto.flight_num = null
        }

        return {
            mission: missionDto,
            flightline_events: flightlineEventDto,
            // TODO - remove only one limit
            mission_shipping_info: shippingInfoDto[0],
        }
    }

    static getMissionName = (
        sensor?: string | number | null,
        date?: string | null,
        flightNum?: number | null,
        channel?: string | null,
    ) => {
        const sensorLabel = sensor ?? "<Sensor>"
        const dateLabel = compactFlownDate(date) ?? "<Date>"
        const flightNumLabel = flightNum ? `_F${flightNum}` : ""
        const channelLabel = channel ? ` (${channel})` : ""
        return `${sensorLabel}_${dateLabel}${flightNumLabel}${channelLabel}`
    }
}

export const shippingInfoMissingFields = (shippingInfo: MissionShippingInfoWithReceiptDto, isVendorFlow: boolean) => {
    const fieldsMissing: (keyof MissionShippingInfoWithReceiptDto)[] = []
    if (shippingInfo.office == null) {
        fieldsMissing.push("office")
    }

    if (isVendorFlow) {
        return fieldsMissing
    }

    if (!shippingInfo.delivery_drive_name) {
        fieldsMissing.push("delivery_drive_name")
    }
    if (!shippingInfo.raw_storage_size_gb) {
        fieldsMissing.push("raw_storage_size_gb")
    }
    return fieldsMissing
}
